import React, { useState, useEffect } from 'react';
import OpeningBalanceCollection from './OpeningBalanceCollection';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useGetOpeningBalance, useSetOpeningBalance } from '../../../services/loanService';
import { useGetCustomerById } from '../../../services/customerService';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { toLocalDateString } from '../../../utils/localDate';

const openingBalanceSchema = z.object({
  openingBalance: z.string()
    .min(1, 'Opening balance is required')
    .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
      message: 'Opening balance must be greater than 0'
    }),
  openingBalanceDate: z.string()
    .min(1, 'Date is required')
    .refine((val) => !isNaN(Date.parse(val)), {
      message: 'Please select a valid date'
    })
});

type OpeningBalanceFormData = z.infer<typeof openingBalanceSchema>;

const CustomerOpeningBalance: React.FC<{ customerBasePath?: string; customerListLabel?: string }> = ({ customerBasePath = '/loan-customers', customerListLabel = 'Business Customers' }) => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();

  const { data: openingBalance, isLoading, error, refetch } = useGetOpeningBalance(
    customerId || '',
    { query: { enabled: !!customerId } }
  ) as { data: any; isLoading: boolean; error: { message?: string } | null; refetch: () => void };

  const { data: customer, isLoading: isLoadingCustomer } = useGetCustomerById(
    customerId || '',
    { query: { enabled: !!customerId } }
  ) as { data: any; isLoading: boolean };

  const setOpeningBalanceMutation = useSetOpeningBalance({
    mutation: {
      onSuccess: () => {
        const hasExisting = openingBalance && openingBalance.openingBalance !== undefined;
        toast.success(hasExisting ? 'Opening balance updated successfully' : 'Opening balance set successfully');
        refetch();
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to set opening balance');
      }
    }
  });

  const { register, handleSubmit, formState: { errors }, reset } = useForm<OpeningBalanceFormData>({
    resolver: zodResolver(openingBalanceSchema),
    defaultValues: {
      openingBalance: '',
      openingBalanceDate: toLocalDateString()
    }
  });

  // Update form when opening balance data is loaded
  useEffect(() => {
    if (openingBalance && openingBalance.openingBalance !== undefined) {
      reset({
        openingBalance: openingBalance.openingBalance.toString(),
        openingBalanceDate: openingBalance.openingBalanceDate || toLocalDateString()
      });
    }
  }, [openingBalance, reset]);

  const onSubmit = async (data: OpeningBalanceFormData) => {
    if (!customerId) {
      toast.error('Customer ID is required');
      return;
    }

    try {
      await setOpeningBalanceMutation.mutateAsync({
        customerPartyId: customerId,
        data: {
          openingBalance: Number(data.openingBalance),
          openingBalanceDate: data.openingBalanceDate
        }
      });
    } catch (error) {
      console.error('Error setting opening balance:', error);
    }
  };

  const customerName = (customer?.info as any)?.registeredName || ((customer?.info as any)?.firstName && (customer?.info as any)?.lastName ? `${(customer?.info as any)?.firstName} ${(customer?.info as any)?.lastName}` : null) || customer?.customerNumber || 'Customer';

  if (isLoading || isLoadingCustomer) {
    return (
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading opening balance...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const errorMessage = (error as any)?.message || 'Failed to load opening balance';
    return (
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <div className="text-center py-5">
            <i className="fa fa-exclamation-triangle fa-3x text-danger mb-3"></i>
            <h5>Error Loading Opening Balance</h5>
            <p className="text-muted">{errorMessage}</p>
            <Button variant="primary" onClick={() => navigate(customerBasePath)}>
              <i className="fa fa-arrow-left me-2"></i>
              Back to Customers
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const hasOpeningBalance = openingBalance && openingBalance.openingBalance !== undefined;
  const isApplied = openingBalance?.openingBalanceApplied === true;
  const canEdit = !hasOpeningBalance || !isApplied;

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
        <div className="container-fluid">
          <div className="row g-3 align-items-center">
            <div className="col">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb bg-transparent mb-2">
                  <li className="breadcrumb-item">
                    <Link className="text-white" to="/">Home</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link className="text-white" to={customerBasePath}>{customerListLabel}</Link>
                  </li>
                  <li className="breadcrumb-item active text-white" aria-current="page">Opening Balance</li>
                </ol>
              </nav>
              <h2 className="mb-0 text-white fw-bold">Customer Opening Balance</h2>
              {customerName && (
                <p className="text-white-50 mb-0 mt-1">
                  <i className="fa fa-user me-2"></i>
                  {customerName}
                </p>
              )}
            </div>
            <div className="col-auto">
              <Button
                variant="light"
                onClick={() => navigate(customerBasePath)}
                className="shadow-sm"
              >
                <i className="fa fa-arrow-left me-2"></i>
                Back to Customers
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-0 py-3">
                  <h5 className="mb-0 fw-bold d-flex align-items-center">
                    <i className="fa fa-money text-primary me-2"></i>
                    {hasOpeningBalance && isApplied ? 'Opening Balance Details' : canEdit ? 'Set Opening Balance' : 'Update Opening Balance'}
                  </h5>
                </Card.Header>
                <Card.Body className="p-4">
                  {hasOpeningBalance && isApplied ? (
                    // View Mode - Opening Balance Already Applied
                    <div>
                      <div className="alert alert-info mb-4">
                        <div className="d-flex">
                          <div className="me-3">
                            <i className="fa fa-info-circle fa-lg"></i>
                          </div>
                          <div>
                            <p className="mb-0">
                              <strong>Opening balance has already been applied for this customer.</strong>
                            </p>
                            <small>Once an opening balance is applied, it cannot be modified. You can only view the details below.</small>
                          </div>
                        </div>
                      </div>

                      <div className="row g-4">
                        <div className="col-md-6">
                          <div className="d-flex align-items-start p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="flex-shrink-0 me-3">
                              <div className="rounded-circle p-2" style={{ backgroundColor: '#e7f3ff', border: '1px solid rgba(0,123,255,0.2)' }}>
                                <i className="fa fa-money text-primary"></i>
                              </div>
                            </div>
                            <div className="flex-grow-1">
                              <small className="text-muted d-block mb-1">Opening Balance Amount</small>
                              <div className="fw-bold text-primary" style={{ fontSize: '1.25rem' }}>
                                {formatCurrency(openingBalance?.openingBalance || 0)}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="d-flex align-items-start p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="flex-shrink-0 me-3">
                              <div className="rounded-circle p-2" style={{ backgroundColor: '#fff3cd', border: '1px solid rgba(255,193,7,0.2)' }}>
                                <i className="fa fa-calendar text-warning"></i>
                              </div>
                            </div>
                            <div className="flex-grow-1">
                              <small className="text-muted d-block mb-1">Opening Balance Date</small>
                              <div className="fw-semibold">
                                {openingBalance?.openingBalanceDate ? formatDate(openingBalance.openingBalanceDate) : 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="d-flex align-items-start p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <div className="flex-shrink-0 me-3">
                              <div className={`rounded-circle p-2 ${openingBalance?.openingBalanceApplied ? 'bg-success bg-opacity-10' : 'bg-warning bg-opacity-10'}`}>
                                <i className={`fa ${openingBalance?.openingBalanceApplied ? 'fa-check-circle text-success' : 'fa-clock text-warning'}`}></i>
                              </div>
                            </div>
                            <div className="flex-grow-1">
                              <small className="text-muted d-block mb-1">Status</small>
                              <div>
                                <span className={`badge ${openingBalance?.openingBalanceApplied ? 'bg-success' : 'bg-warning'} px-3 py-2`}>
                                  {openingBalance?.openingBalanceApplied ? 'Applied' : 'Pending Application'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                      </div>
                    </div>
                  ) : (
                    // Add/Edit Mode - Can Set or Update Opening Balance
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className={`alert mb-4 ${hasOpeningBalance ? 'alert-warning' : 'alert-warning'}`}>
                        <div className="d-flex">
                          <div className="me-3">
                            <i className={`fa ${hasOpeningBalance ? 'fa-exclamation-triangle' : 'fa-exclamation-triangle'} fa-lg`}></i>
                          </div>
                          <div>
                            <p className="mb-0">
                              <strong>
                                {hasOpeningBalance 
                                  ? 'Opening balance has been set but not yet applied. You can update it before it is applied.'
                                  : 'No opening balance has been set for this customer.'}
                              </strong>
                            </p>
                            <small>
                              {hasOpeningBalance
                                ? 'Update the opening balance amount and date. Once applied, it cannot be modified.'
                                : 'Set the opening balance amount and date. This action cannot be undone once applied.'}
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="row g-4">
                        <div className="col-md-6">
                          <Form.Group className="mb-4">
                            <Form.Label className="fw-semibold mb-2">
                              Opening Balance Amount (ZAR) <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="input-group input-group-lg" style={{ borderRadius: '0.5rem', overflow: 'hidden' }}>
                              <span className="input-group-text bg-white border-0 px-4">
                                <i className="fa fa-rand text-primary fa-lg" style={{ display: 'block' }}></i>
                              </span>
                              <Form.Control
                                type="number"
                                {...register('openingBalance')}
                                isInvalid={!!errors.openingBalance}
                                placeholder="0.00"
                                step="0.01"
                                min="0.01"
                                style={{ fontSize: '1.1rem', fontWeight: 500 }}
                                className="border-0"
                              />
                            </div>
                            {errors.openingBalance && (
                              <Form.Control.Feedback type="invalid" className="d-block mt-2">
                                <i className="fa fa-exclamation-circle me-1"></i>
                                {errors.openingBalance.message}
                              </Form.Control.Feedback>
                            )}
                            <small className="text-muted d-block mt-2">
                              <i className="fa fa-info-circle me-1"></i>
                              Enter the opening balance amount in South African Rand (ZAR)
                            </small>
                          </Form.Group>
                        </div>

                        <div className="col-md-6">
                          <Form.Group className="mb-4">
                            <Form.Label className="fw-semibold mb-2">
                              Opening Balance Date <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="input-group input-group-lg" style={{ borderRadius: '0.5rem', overflow: 'hidden' }}>
                              <span className="input-group-text bg-white border-0 px-4">
                                <i className="fa fa-calendar text-primary fa-lg" style={{ display: 'block' }}></i>
                              </span>
                              <Form.Control
                                type="date"
                                {...register('openingBalanceDate')}
                                isInvalid={!!errors.openingBalanceDate}
                                style={{ fontSize: '1.1rem', fontWeight: 500 }}
                                className="border-0"
                              />
                            </div>
                            {errors.openingBalanceDate && (
                              <Form.Control.Feedback type="invalid" className="d-block mt-2">
                                <i className="fa fa-exclamation-circle me-1"></i>
                                {errors.openingBalanceDate.message}
                              </Form.Control.Feedback>
                            )}
                            <small className="text-muted d-block mt-2">
                              <i className="fa fa-info-circle me-1"></i>
                              Date when the opening balance was migrated (defaults to today if not provided)
                            </small>
                          </Form.Group>
                        </div>
                      </div>

                      <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
                        <Button
                          variant="outline-secondary"
                          onClick={() => navigate(customerBasePath)}
                          className="px-5 py-2 rounded-pill"
                        >
                          <i className="fa fa-times me-2"></i>
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={setOpeningBalanceMutation.isPending}
                          className="px-5 py-2 rounded-pill shadow-sm fw-semibold"
                          style={{ minWidth: '180px' }}
                        >
                          {setOpeningBalanceMutation.isPending ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {hasOpeningBalance ? 'Updating...' : 'Setting...'}
                            </>
                          ) : (
                            <>
                              <i className="fa fa-check-circle me-2"></i>
                              {hasOpeningBalance ? 'Update Opening Balance' : 'Set Opening Balance'}
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  )}
                </Card.Body>
              </Card>

              {/*
                Collecting the balance, not just recording it. The person who captures an
                opening balance is the one who then has to collect it, so activation, payments
                and interest belong on the same screen rather than somewhere else.
              */}
              {customerId && (
                <OpeningBalanceCollection
                  customerPartyId={customerId}
                  customerName={customerName}
                />
              )}
        </div>
      </div>
    </>
  );
};

export default CustomerOpeningBalance;

